/* Bold Contact — oversize CONTACT headline, thick accent underlines on
   inputs, solid accent-block submit. */

import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export default function BoldContact() {
  const { settings } = useSiteSettings();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!form.email || !form.message) return;
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Booked into the queue.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="bg-[color:var(--tenant-bg,#FAF9F6)] text-[color:var(--tenant-fg,#0E0D0C)] min-h-screen pt-20 pb-32" data-testid="bold-contact" style={{ fontFamily: "var(--tenant-sans)" }}>
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12 pt-12">
        <div className="text-[10px] uppercase tracking-[0.45em] text-[color:var(--tenant-accent)] mb-3">
          ACT — Direct line
        </div>
        <h1
          className="font-extrabold uppercase leading-[0.92] tracking-tighter mb-12"
          style={{
            fontFamily: "var(--tenant-sans)",
            fontSize: "clamp(4rem, 14vw, 13rem)",
            color: "var(--tenant-accent, #C9A961)",
          }}
        >
          Contact
        </h1>
      </div>

      <div className="max-w-3xl mx-auto px-6 lg:px-12">
        <form onSubmit={submit} className="space-y-12" data-testid="bold-contact-form">
          {[
            { name: "name", label: "Your name", type: "text" },
            { name: "email", label: "Email address", type: "email" },
          ].map((f) => (
            <div key={f.name}>
              <label className="block text-[10px] uppercase tracking-[0.45em] mb-3 font-bold">
                {f.label}
              </label>
              <input
                type={f.type}
                value={form[f.name]}
                onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                className="w-full bg-transparent border-0 py-3 text-2xl lg:text-3xl font-bold uppercase tracking-tight focus:outline-none"
                style={{ borderBottom: "3px solid var(--tenant-accent, #C9A961)" }}
                data-testid={`bold-contact-${f.name}`}
              />
            </div>
          ))}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.45em] mb-3 font-bold">
              What you want
            </label>
            <textarea
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full bg-transparent border-0 py-3 text-lg focus:outline-none resize-none"
              style={{ borderBottom: "3px solid var(--tenant-accent, #C9A961)" }}
              data-testid="bold-contact-message"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            className="px-9 py-5 text-[12px] uppercase tracking-[0.42em] font-extrabold text-white disabled:opacity-50 hover:opacity-90 transition-opacity"
            style={{ background: "var(--tenant-accent, #C9A961)" }}
            data-testid="bold-contact-submit"
          >
            {sending ? "Sending…" : "Send the message →"}
          </button>
        </form>
        {settings?.contact_email && (
          <div className="mt-20 pt-10 border-t-4" style={{ borderColor: "var(--tenant-accent, #C9A961)" }}>
            <div className="text-[10px] uppercase tracking-[0.45em] font-bold mb-2">Direct</div>
            <a href={`mailto:${settings.contact_email}`} className="text-2xl lg:text-3xl font-extrabold uppercase hover:opacity-70 transition-opacity">
              {settings.contact_email}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
